import React, { useMemo } from "react";
import { useNavigate, useLocation, useParams, useMatch } from "react-router";

/**
 * react-router v7 no longer injects history/location/match as props (v4
 * behavior). This wrapper reconstructs the same v4 shape from hooks so class
 * components don't need a hooks rewrite.
 *
 * routePattern is optional: pass the exact `path` used on the <Route> this
 * component is mounted at when the component reads `match.url`/`match.path`
 * (v4's "portion of the URL matched by this route"), since that needs
 * useMatch(pattern) to compute correctly. Components that only read
 * `match.params` don't need it - useParams() alone is pattern-independent.
 */
export default function withRouterCompat(WrappedComponent, routePattern) {
  return function RouterCompatWrapper(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const routeMatch = useMatch(routePattern || "*");

    // navigate is stable across renders, so this only changes identity when
    // the router instance itself changes - keeps consuming class components'
    // connect()/PureComponent checks from treating every render as a change.
    const history = useMemo(
      () => ({
        get length() {
          return window.history.length;
        },
        push: (path, state) => navigate(path, { state }),
        replace: (path, state) => navigate(path, { state, replace: true }),
        goBack: () => navigate(-1),
        go: (n) => navigate(n),
      }),
      [navigate],
    );

    const match = useMemo(
      () =>
        routePattern && routeMatch
          ? {
              params: routeMatch.params,
              path: routeMatch.pattern.path,
              url: routeMatch.pathnameBase,
              isExact: routeMatch.pathname === routeMatch.pathnameBase,
            }
          : {
              params,
              path: location.pathname,
              url: location.pathname,
              isExact: true,
            },
      [routePattern, routeMatch, params, location.pathname],
    );

    return (
      <WrappedComponent
        {...props}
        history={history}
        location={location}
        match={match}
      />
    );
  };
}
